import { format } from "date-fns";
import { useCallback, useEffect, useRef } from "react";
import Calendar, { CalendarProps } from "react-calendar";
import { Transition } from "react-transition-group";
import { TransitionProps } from "react-transition-group/Transition";
import PortalledContent from "shared/components/PortalledContent";

const minDate = new Date(new Date().valueOf() - 365 * 24 * 60 * 60 * 1000);
const maxDate = new Date();
// const onMonthChange = () => {};
// const onYearChange = () => {};

/**
 * `$transitionShortMedium` in,
 * `$transitionShort` out
 */
const TIMEOUTS: TransitionProps["timeout"] = {
  enter: 0,
  appear: 450,
  exit: 300,
};

interface DateSelectorProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  date: CalendarProps["value"];
  onChange: CalendarProps["onChange"];
}

export const DateSelector: React.FC<DateSelectorProps> = ({
  open,
  setOpen,
  date,
  onChange,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);

  const openCalendar = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  useEffect(() => {
    const element = document.getElementById("calendar_portal");
    if (parentRef.current && nodeRef.current && element) {
      const {
        top: parentTop,
        left: parentLeft,
        height: parentHeight,
      } = parentRef.current.getBoundingClientRect();

      // Initial offsets and picker
      const initialOffset = parentHeight + 10;
      const { height: pickerHeight, width: pickerWidth } =
        nodeRef.current.getBoundingClientRect();

      let newLeft = parentLeft + 0;
      if (window.innerWidth < 568) {
        newLeft = (window.innerWidth - pickerWidth) / 2;
      }

      const offset = initialOffset + pickerHeight;
      let newTop = parentTop;
      // If the calendar would show below the viewport, move it above
      if (parentTop + offset > window.innerHeight) {
        newTop = newTop - offset;
      } else {
        newTop = newTop + initialOffset;
      }

      // Set position
      nodeRef.current.style.transform = `translate3d(${newLeft}px, ${newTop}px, 0)`;
    }
  }, [open]);

  return (
    <>
      <div ref={parentRef} className="DisplayFlex VSmallGap">
        {/* Month */}
        <span className="DateSelectorLabel" onClick={openCalendar}>
          {date && format(date as Date, "LLLL")}
        </span>
        {/* Year */}
        <span className="DateSelectorLabel" onClick={openCalendar}>
          {date && format(date as Date, "yyyy")}
        </span>
      </div>
      {/* Calendar picker */}
      <PortalledContent
        targetPortal="calendar_portal"
        children={
          <Transition
            nodeRef={nodeRef}
            in={open}
            timeout={TIMEOUTS}
            // NOTE: Don't unmount, `pickerHeight` relies on it to stay in DOM
            // mountOnEnter
            // unmountOnExit
          >
            {(transitionState) => (
              <>
                <div
                  className={`SharedUnderlay ClearUnderlay ${transitionState}`}
                  onClick={() => {
                    setOpen(false);
                  }}
                />
                <div ref={nodeRef} className={`DatePicker ${transitionState}`}>
                  <Calendar
                    value={date}
                    onChange={onChange}
                    minDate={minDate}
                    maxDate={maxDate}
                    defaultView="year"
                    maxDetail="year"
                    minDetail="decade"
                  />
                </div>
              </>
            )}
          </Transition>
        }
      />
    </>
  );
};
